import React from 'react';
import { Tooltip } from '@heroui/react';
import { RevshareOrder } from '../../../../api/client/clientRevshareService';
import { useTranslations } from '../../../../contexts/translation.context';
import { ChartSquare, UserCheckIc, GroupUserIc } from '../../../../components/icons';
import { ResponsiveTooltip, Typography } from '../../../../components';

interface StatisticsCellProps {
  campaign: RevshareOrder;
}

const formatPercent = (value: number, total: number): string => {
  if (!total) return '0%';
  const percent = (value / total) * 100;
  return `${percent.toFixed(2)}%`;
};

// Helper for tooltip content - matching OrderStatsTooltip design
const CampaignStatsTooltip: React.FC<{ campaign: RevshareOrder }> = ({ campaign }) => {
  const { t } = useTranslations();

  const subscribedCount = campaign.subscribed_count || 0;
  const verifiedUsers = campaign.verified_users_count || subscribedCount;
  const transactionsStatistic = campaign.transactions_statistic || {
    '1': 0,
    '2': 0,
    '5': 0,
    '500': 0,
    total: '0',
    count: 0,
  };

  const purchasesCount = transactionsStatistic.count || 0;
  const purchases500 = transactionsStatistic['500'] || 0;

  // TODO: Эти поля должны прийти из API (пока используются значения по умолчанию)
  const repeatedSubscriptions = campaign.repeated_subscriptions_count || 0;
  const messagesCount =
    campaign.subs?.with_gte_3_chat_messages_count || campaign.messages_3_plus_count || 0;

  // Use verified_users_count as total for percentage calculations (matching Orders pattern)
  const totalFans = verifiedUsers || subscribedCount;

  const stats = [
    {
      label: t('campaigns.stats.subscriptions') || 'Подписки',
      value: verifiedUsers,
      percentage: formatPercent(verifiedUsers, totalFans),
    },
    {
      label: t('campaigns.stats.repeatedSubscriptions') || 'Повторные подписки',
      value: repeatedSubscriptions,
      percentage: formatPercent(repeatedSubscriptions, totalFans),
    },
    {
      label: t('campaigns.stats.purchases') || 'Покупки',
      value: purchasesCount,
      percentage: formatPercent(purchasesCount, totalFans),
    },
    {
      label: t('campaigns.stats.purchases500') || 'Покупки на $500',
      value: purchases500,
      percentage: formatPercent(purchases500, totalFans),
    },
    {
      label: t('campaigns.stats.messages3') || '3 сообщения',
      value: messagesCount,
      percentage: formatPercent(messagesCount, totalFans),
    },
  ];

  return (
    <div className="">
      <div className="mb-2">
        <Typography variant="body2" className="text-auth-ink">
          {t('campaigns.stats.title') || 'Статистика кампании'}
        </Typography>
      </div>

      <div className="flex items-center mb-2">
        <div className="w-[10rem]">
          <Typography variant="label3" className="text-auth-muted">
            {t('campaigns.stats.parameter') || 'Параметр'}
          </Typography>
        </div>
        <div className="w-[7.5rem]">
          <Typography variant="label3" className="text-auth-muted">
            {t('campaigns.stats.fans') || 'Фанатов'}
          </Typography>
        </div>
        <div className="w-[7.5rem]">
          <Typography variant="label3" className="text-auth-muted">
            %
          </Typography>
        </div>
      </div>

      <div>
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center py-1.5">
            <div className="w-[10rem]">
              <Typography variant="body3" className="text-auth-muted">
                {stat.label}
              </Typography>
            </div>
            <div className="w-[7.5rem]">
              <Typography variant="label3" className="text-auth-ink">
                {stat.value}
              </Typography>
            </div>
            <div className="w-[7.5rem]">
              <Typography variant="label3" className="text-auth-ink">
                {stat.percentage}
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatisticsCell: React.FC<StatisticsCellProps> = ({ campaign }) => {
  const { t } = useTranslations();

  // Use new API response structure with fallback to legacy
  const subscribedCount = campaign.subscribed_count || campaign.statistics?.subscribers || 0;
  const transactionsStatistic = campaign.transactions_statistic;
  const revenue = transactionsStatistic
    ? parseFloat(transactionsStatistic.total) || 0
    : campaign.statistics?.revenue || 0;
  // cost = total в transactions_statistic (как в Subscribers)
  const cost = transactionsStatistic
    ? parseFloat(transactionsStatistic.total) || 0
    : campaign.statistics?.cost || 0;

  // Calculate ARPU on frontend
  // ARPU (план) = cost / subscribed_count (если есть cost)
  const arpu =
    subscribedCount > 0 && cost > 0 ? parseFloat((cost / subscribedCount).toFixed(2)) : 0;
  // ARPU($) (факт) = revenue / subscribed_count
  const arpuUsd = subscribedCount > 0 ? parseFloat((revenue / subscribedCount).toFixed(2)) : 0;

  // Total fans = subscribed_count
  const totalFans = subscribedCount;
  // Verified users (use from API if available, otherwise use subscribed_count)
  const verifiedUsers = campaign.verified_users_count || subscribedCount;

  return (
    <div className="flex flex-col gap-1">
      <ResponsiveTooltip content={<CampaignStatsTooltip campaign={campaign} />} className="p-4">
        <div className="flex items-center gap-2 cursor-pointer">
          {/* <UserCheckIc className="text-gray-600" />
          <Typography variant="body3" className="text-gray-900">
            {verifiedUsers}/{totalFans}
          </Typography> */}

          <GroupUserIc className="text-gray-600" />
          <Typography variant="body3" className="text-gray-900">
            {verifiedUsers}
          </Typography>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <g clip-path="url(#clip0_58039_123170)">
              <circle cx="6" cy="6" r="5" stroke="#71717A" />
              <path d="M6 8.5V8.75V9" stroke="#71717A" stroke-linecap="round" />
              <path d="M6 3V3.25V3.5" stroke="#71717A" stroke-linecap="round" />
              <path
                d="M7.5 4.75C7.5 4.05964 6.82843 3.5 6 3.5C5.17157 3.5 4.5 4.05964 4.5 4.75C4.5 5.44036 5.17157 6 6 6C6.82843 6 7.5 6.55964 7.5 7.25C7.5 7.94036 6.82843 8.5 6 8.5C5.17157 8.5 4.5 7.94036 4.5 7.25"
                stroke="#71717A"
                stroke-linecap="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_58039_123170">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <Typography variant="body3" className="text-gray-900">
            {parseFloat(transactionsStatistic.total || '0').toFixed(2)}
          </Typography>
          <ChartSquare />
        </div>
      </ResponsiveTooltip>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('campaigns.table.transactions') || 'Транзакции'}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          ${revenue.toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('campaigns.table.cost') || 'Стоимость'}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          ${cost.toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('campaigns.table.arpu') || 'ARPU'}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          ${arpu.toFixed(2)}
        </Typography>
      </div>

      <div className="flex items-center gap-2">
        <Typography variant="body3" className="text-auth-muted">
          {t('campaigns.table.arpuUsd') || 'ARPU($)'}:
        </Typography>
        <Typography variant="body2" className="md:font-semibold">
          ${arpuUsd.toFixed(2)}
        </Typography>
      </div>
    </div>
  );
};

export default StatisticsCell;
